import { groupBySection, packColumns, type MenuBlock } from '../paginate.js';
import { featuredItem } from './cafe-panels.js';
import {
  bilingual,
  description,
  hasTitle,
  image,
  listColumns,
  listCss,
  listItem,
  priceBlock,
  sectionSubtitle,
  sectionTitle,
  type MenuTemplate,
  type RenderContext,
} from './common.js';

/**
 * An Italian trattoria card: cream paper, a tricolour stripe, a serif brand header, the
 * featured dish framed like a photograph, and serif section headings between ornament rules.
 */

const S = '.mb-t-trattoria';

function hero(ctx: RenderContext): string {
  const item = featuredItem(ctx.board);
  if (!item) return '';
  return [
    '<div class="tr-hero">',
    `<div class="tr-frame">${image(ctx, item.imageMediaId, 'tr-photo')}</div>`,
    '<p class="tr-chef">Chef’s choice</p>',
    bilingual(ctx, item.title, item.titleAr, 'tr-hero-name'),
    description(ctx, item, 'tr-hero-desc'),
    `<div class="tr-hero-price">${priceBlock(ctx, item, 'tr')}</div>`,
    '</div>',
  ].join('');
}

function block(ctx: RenderContext, b: MenuBlock): string {
  const head = hasTitle(b.section)
    ? `<div class="tr-head">${sectionTitle(ctx, b.section, 'tr-title')}${b.continued ? '' : sectionSubtitle(ctx, b.section, 'tr-sub')}</div>`
    : '';
  return `<div class="tr-sec">${head}${b.items.map((i) => listItem(ctx, i, 'tr')).join('')}</div>`;
}

export const trattoria: MenuTemplate = {
  id: 'trattoria',
  name: 'Trattoria',
  description: 'Cream paper, an Italian stripe, serif headings and the chef’s dish framed like a photograph. Made for pasta.',
  defaultTheme: {
    backgroundColor: '#f6efe2',
    surfaceColor: '#ffffff',
    textColor: '#3a2f27',
    primaryColor: '#2c241e',
    accentColor: '#b23a2e',
    fontId: 'classic',
    backgroundMediaId: null,
  },
  variants: [
    { id: 'tricolore', name: 'Tricolore', theme: {} },
    { id: 'olive', name: 'Olive grove', theme: { accentColor: '#5c6b2e', backgroundColor: '#f3efe0' } },
    { id: 'nero', name: 'Nero', theme: { backgroundColor: '#1d1916', surfaceColor: '#2a2420', textColor: '#efe6d8', primaryColor: '#fff8ec', accentColor: '#d9584a' } },
    { id: 'blu', name: 'Amalfi blue', theme: { backgroundColor: '#f7f5ef', accentColor: '#24538a', primaryColor: '#1d2c42' } },
  ],
  cuisines: ['pasta'],
  uses: { featured: true, sectionImages: false, brand: true, decorations: [] },

  paginate(board, orientation) {
    const portrait = orientation === 'portrait';
    const featured = featuredItem(board);
    return packColumns(
      groupBySection(board, featured?.id),
      listColumns(board, {
        count: 2,
        capacity: portrait ? (featured ? 66 : 96) : 52,
        heading: 5.2,
        item: { nameEm: 1.4, descEm: 0.85, perLine: portrait ? 40 : 46, gap: 1.3 },
      }),
      { balance: true },
    );
  },

  render(ctx) {
    const { board } = ctx;
    const header = [
      '<div class="tr-header">',
      bilingual(ctx, board.brand.name ?? board.settings.title ?? board.name, board.brand.nameAr ?? board.settings.titleAr, 'tr-brand'),
      bilingual(ctx, board.brand.tagline, board.brand.taglineAr, 'tr-tagline'),
      '</div>',
    ].join('');
    const lists = `<div class="tr-cols">${ctx.page.columns
      .map((c) => `<div class="tr-col">${c.map((b) => block(ctx, b)).join('')}</div>`)
      .join('')}</div>`;
    return `<div class="tr-stripe"><i></i><i></i><i></i></div><div class="tr-body">${hero(ctx)}<div class="tr-main">${header}${lists}</div></div>`;
  },

  css: `
${S}{padding:0;color:var(--mb-text);
  background-image:radial-gradient(circle at 20% 15%,rgba(255,255,255,.55),transparent 45%),
    radial-gradient(circle at 85% 90%,rgba(120,80,40,.08),transparent 50%)}
${S} .tr-stripe{display:flex;height:.9em;flex-shrink:0}
${S} .tr-stripe i{flex:1}
${S} .tr-stripe i:nth-child(1){background:#2f7d3b}
${S} .tr-stripe i:nth-child(2){background:#f4f1e8}
${S} .tr-stripe i:nth-child(3){background:#c0392b}
${S}.mb-v-olive .tr-stripe i:nth-child(1),${S}.mb-v-olive .tr-stripe i:nth-child(3){background:var(--mb-accent)}
${S}.mb-v-blu .tr-stripe i{background:var(--mb-accent)}
${S}.mb-v-blu .tr-stripe i:nth-child(2){background:#f4f1e8}
${S} .tr-body{flex:1;min-height:0;display:grid;grid-template-columns:34% 1fr;gap:3.5em;padding:2.6em 3.4em 2.4em}
${S}.mb-portrait .tr-body{grid-template-columns:1fr;grid-template-rows:auto 1fr;gap:2em}
${S} .tr-body:not(:has(.tr-hero)){grid-template-columns:1fr}
${S} .tr-main{min-width:0;min-height:0;display:flex;flex-direction:column}
${S} .tr-header{text-align:center;margin-bottom:1.6em}
${S} .tr-brand{font-family:var(--mb-font-heading);font-weight:700;font-size:3.6em;color:var(--mb-primary);line-height:1.05}
${S} .tr-brand.mb-2nd{font-size:2.2em}
${S} .tr-tagline{font-style:italic;color:var(--mb-accent);font-size:1.2em;margin-top:.3em}
${S} .tr-cols{flex:1;min-height:0;display:grid;grid-template-columns:1fr 1fr;gap:3em}
${S} .tr-col{min-width:0;display:flex;flex-direction:column;gap:1.8em}
${S} .tr-head{text-align:center;margin-bottom:1em}
${S} .tr-title{display:flex;align-items:center;gap:.7em;justify-content:center;font-family:var(--mb-font-heading);font-weight:700;
  font-size:1.9em;text-transform:uppercase;letter-spacing:.14em;color:var(--mb-accent)}
${S} .tr-title:before,${S} .tr-title:after{content:'';flex:1;height:.06em;background:currentColor;opacity:.5}
${S} .tr-title.mb-2nd{font-size:1.4em;letter-spacing:0}
${S} .tr-sub{font-style:italic;font-size:1em;opacity:.75;margin-top:.2em}
${listCss(S, 'tr', { name: '1.4em', price: '1.3em', desc: '.85em', leader: '.12em dotted rgba(60,40,30,.35)' })}
${S}.mb-v-nero .tr-leader{border-bottom-color:rgba(255,240,220,.3)}
${S} .tr-item{margin-bottom:1.1em}
${S} .tr-name{font-family:var(--mb-font-heading);font-weight:700;color:var(--mb-primary)}
${S} .tr-price{font-family:var(--mb-font-heading);font-weight:700;color:var(--mb-accent)}
${S} .tr-desc{font-style:italic}
${S} .tr-hero{display:flex;flex-direction:column;align-items:center;text-align:center;min-height:0}
${S}.mb-portrait .tr-hero{display:grid;grid-template-columns:55% 1fr;grid-template-rows:auto auto auto 1fr;column-gap:2.4em;text-align:start;align-items:start}
${S}.mb-portrait .tr-frame{grid-row:1 / span 4}
${S} .tr-frame{width:100%;padding:1em 1em 1.4em;background:var(--mb-surface);box-shadow:0 1em 2.4em rgba(60,40,20,.22);
  transform:rotate(-2deg);margin-bottom:1.6em}
${S} .tr-photo{display:block;width:100%;aspect-ratio:4/3.4;object-fit:cover}
${S} .tr-chef{font-family:var(--mb-font-script);color:var(--mb-accent);font-size:1.9em;line-height:1}
${S} .tr-hero-name{font-family:var(--mb-font-heading);font-weight:700;font-size:2.4em;color:var(--mb-primary);line-height:1.1;margin-top:.15em}
${S} .tr-hero-name.mb-2nd{font-size:1.6em}
${S} .tr-hero-desc{font-style:italic;opacity:.8;margin-top:.4em;max-width:24em}
${S} .tr-hero-price{margin-top:.5em;font-size:1.5em}
`,
};
